// Pure. Faithful to htn_regimen_axes.py. No React, no external app imports.

const BAND_DETAILS = {
  balanced: {
    bandLabel: 'Balanced',
    tone: 'rgb(45, 212, 160)',
  },
  hypokalemic: {
    bandLabel: 'Hypokalemic',
    tone: 'rgb(96, 165, 250)',
  },
  hyperkalemic: {
    bandLabel: 'Hyperkalemic',
    tone: 'rgb(248, 113, 113)',
  },
};

const STATUS_LABELS = {
  severe_hypo: 'SEVERE HYPO',
  hypo: 'HYPO',
  normal: 'NORMAL',
  hyper: 'HYPER',
  severe_hyper: 'SEVERE HYPER',
};

// Mirrors POTASSIUM_HYPO_CHANNEL / POTASSIUM_HYPER_CHANNEL grades in
// htn_regimen_axes.py. Only used when the wire omits probability/magnitude.
//
// Keyed by BAND as well as severity, because hyperkalemia costs twice what
// hypokalemia does at the same severity. The previous fallback was keyed by
// severity alone, which was correct only while both poles shared one number;
// left that way it would have quietly halved every hyper band the moment the
// wire went quiet, and a fallback that renders a plausible wrong number is worse
// than one that renders nothing.
const GRADE_FALLBACKS = {
  hypokalemic: {
    warning: { probability: 0.25, magnitude: 5 },
    critical: { probability: 0.5, magnitude: 10 },
  },
  hyperkalemic: {
    warning: { probability: 0.25, magnitude: 10 },
    critical: { probability: 0.5, magnitude: 20 },
  },
};

function bandFor(region, pole) {
  const regionKey = typeof region === 'string' ? region : '';
  if (regionKey.includes('hyper')) {
    return 'hyperkalemic';
  }
  if (regionKey.includes('hypo')) {
    return 'hypokalemic';
  }
  if (regionKey === 'normal') {
    return 'balanced';
  }

  if (pole === 'hyper' || pole === 'high') {
    return 'hyperkalemic';
  }
  if (pole === 'hypo' || pole === 'low') {
    return 'hypokalemic';
  }
  return 'balanced';
}

function finiteBiteNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function potassiumBandModel({
  region = 'normal',
  severity = 'safe',
  pole = 'balanced',
  net = 0,
  serum = 4.2,
  bites = [],
  bitesEnabled = false,
} = {}) {
  const bandKey = bandFor(region, pole);
  const { bandLabel, tone } = BAND_DETAILS[bandKey];
  const statusLabel = STATUS_LABELS[region] || 'NORMAL';
  const severityKey = severity || 'safe';
  // A deranged serum potassium is a CHANCE TO TAKE DAMAGE ON A WRONG ANSWER
  // (Edward 2026-07-27). It replaced an always-on amplifier on every incoming
  // enemy attack; the derangement is a consequence of ERROR and lands on the beat
  // the player got something wrong.
  const spikeBite = Array.isArray(bites)
    ? bites.find((bite) => bite?.channel === 'probabilistic_trigger')
    : null;
  const active = Boolean(bitesEnabled && bandKey !== 'balanced' && spikeBite);

  let effectText;
  let effectStat = null;
  if (bandKey === 'balanced') {
    effectText = 'No penalty right now.';
  } else if (!bitesEnabled) {
    effectText = 'Monitoring only. Not active this fight.';
  } else if (active) {
    const fallback = GRADE_FALLBACKS[bandKey]?.[severityKey] || null;
    const probability = finiteBiteNumber(spikeBite.probability) ?? fallback?.probability ?? 0;
    const magnitude = finiteBiteNumber(spikeBite.magnitude) ?? fallback?.magnitude ?? 0;
    const chancePct = Math.round(probability * 100);

    effectStat = { chancePct, probability, magnitude };
    effectText = `${chancePct}% chance to take ${magnitude} damage on a wrong answer.`;
  } else {
    effectText = 'Outside the safe band.';
  }

  const serumNumber = Number(serum);
  const netNumber = Number(net);
  const safeNet = Number.isFinite(netNumber) ? netNumber : 0;

  return {
    bandKey,
    bandLabel,
    statusLabel,
    severityKey,
    tone,
    active,
    effectText,
    effectStat,
    serumLabel: Number.isFinite(serumNumber) ? serumNumber.toFixed(2) : '4.20',
    shiftLabel: safeNet > 0 ? `+${safeNet}` : `${safeNet}`,
  };
}
